/**
 * FraudQueue Component
 * 
 * Main fraud review queue interface with:
 * - Table view of held payments
 * - Filters and sorting
 * - Bulk actions
 * - Risk visualization
 */

import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
  type RowSelectionState,
  type HeaderContext,
  type CellContext,
  type HeaderGroup,
  type Header,
  type Row,
} from '@tanstack/react-table';
import {
  Shield,
  CheckCircle,
  XCircle,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckSquare,
  Square,
  RefreshCw,
  ArrowRight,
} from 'lucide-react';
import { cn, formatCurrency } from '../../lib/utils';
import { useFraudQueue } from '../../hooks/useFraudQueue';
import { FraudDetailPanel } from './FraudDetailPanel';
import type { FraudQueueItem, FraudQueueFilters } from '../../types/fraud';
import type { RiskLevel } from '../../types';

export interface FraudQueueProps {
  customerId: string;
  className?: string;
}

// =============================================================================
// Risk Score Bar Component
// =============================================================================

const RiskScoreBar: React.FC<{ score: number }> = ({ score }) => {
  const percentage = score * 100;
  const color = percentage > 70 ? 'bg-red-500' : percentage > 50 ? 'bg-orange-500' : 'bg-yellow-500';

  return (
    <div className="flex items-center space-x-2">
      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm font-medium w-10">{percentage.toFixed(0)}%</span>
    </div>
  );
};

// =============================================================================
// Filters Panel Component
// =============================================================================

interface FiltersPanelProps {
  filters: FraudQueueFilters;
  onFiltersChange: (filters: FraudQueueFilters) => void;
  stats?: {
    totalHeld: number;
    highRisk: number;
    mediumRisk: number;
    lowRisk: number;
  };
}

const FiltersPanel: React.FC<FiltersPanelProps> = ({ filters, onFiltersChange, stats }) => {
  const riskLevels: RiskLevel[] = ['low', 'medium', 'high', 'critical'];

  const toggleRiskLevel = (level: RiskLevel) => {
    const current = filters.riskLevel || [];
    const updated = current.includes(level)
      ? current.filter((l) => l !== level)
      : [...current, level];
    onFiltersChange({ ...filters, riskLevel: updated });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900 flex items-center">
          <Filter className="w-4 h-4 mr-2" />
          Filters
        </h3>
        <button
          onClick={() => onFiltersChange({})}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Clear all
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Risk Level Filter */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Risk Level</label>
          <div className="space-y-2">
            {riskLevels.map((level) => (
              <label key={level} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={(filters.riskLevel || []).includes(level)}
                  onChange={() => toggleRiskLevel(level)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 capitalize">{level}</span>
                {stats && (
                  <span className="ml-auto text-xs text-gray-500">
                    {level === 'high'
                      ? stats.highRisk
                      : level === 'medium'
                      ? stats.mediumRisk
                      : level === 'low'
                      ? stats.lowRisk
                      : stats.highRisk}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Amount Range Filter */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Amount Range</label>
          <div className="space-y-2">
            <input
              type="number"
              placeholder="Min ($)"
              value={filters.minAmount ? filters.minAmount / 100 : ''}
              onChange={(e) =>
                onFiltersChange({
                  ...filters,
                  minAmount: e.target.value ? parseInt(e.target.value) * 100 : undefined,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Max ($)"
              value={filters.maxAmount ? filters.maxAmount / 100 : ''}
              onChange={(e) =>
                onFiltersChange({
                  ...filters,
                  maxAmount: e.target.value ? parseInt(e.target.value) * 100 : undefined,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Time in Queue Filter */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Time in Queue</label>
          <select
            value={filters.timeInQueue || ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                timeInQueue: e.target.value as any,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All times</option>
            <option value="under_1h">Under 1 hour</option>
            <option value="1h_to_4h">1 to 4 hours</option>
            <option value="4h_to_24h">4 to 24 hours</option>
            <option value="over_24h">Over 24 hours</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Main FraudQueue Component
// =============================================================================

export const FraudQueue: React.FC<FraudQueueProps> = ({ customerId, className }) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'combinedRiskScore', desc: true }]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [showBulkActionConfirm, setShowBulkActionConfirm] = useState<'release' | 'deny' | null>(null);

  const {
    items,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
    selectedCount,
    toggleSelection,
    selectAll,
    deselectAll,
    isSelected,
    releasePayment,
    denyPayment,
    bulkRelease,
    bulkDeny,
    isProcessing,
    usePaymentDetail,
    refetch,
  } = useFraudQueue({ customerId });

  const { data: selectedPayment } = usePaymentDetail(selectedPaymentId);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.paymentId.toLowerCase().includes(query) ||
        item.vendorName.toLowerCase().includes(query) ||
        item.customerName.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  // Table columns
  const columns = useMemo<ColumnDef<FraudQueueItem>[]>(
    () => [
      {
        id: 'select',
        header: (props: HeaderContext<FraudQueueItem, unknown>) => {
          const table = props.table;
          return (
            <button
              onClick={() => {
                if (table.getIsAllRowsSelected()) {
                  deselectAll();
                } else {
                  selectAll(items);
                }
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {table.getIsAllRowsSelected() ? (
                <CheckSquare className="w-5 h-5 text-blue-600" />
              ) : (
                <Square className="w-5 h-5 text-gray-400" />
              )}
            </button>
          );
        },
        cell: (props: CellContext<FraudQueueItem, unknown>) => {
          const row = props.row;
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(row.original.paymentId);
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isSelected(row.original.paymentId) ? (
                <CheckSquare className="w-5 h-5 text-blue-600" />
              ) : (
                <Square className="w-5 h-5 text-gray-400" />
              )}
            </button>
          );
        },
      },
      {
        accessorKey: 'paymentId',
        header: 'Payment ID',
        cell: (props: CellContext<FraudQueueItem, unknown>) => {
          const row = props.row;
          return (
            <button
              onClick={() => setSelectedPaymentId(row.original.paymentId)}
              className="font-mono text-sm text-blue-600 hover:text-blue-700"
            >
              {row.original.paymentId}
            </button>
          );
        },
      },
      {
        accessorKey: 'vendorName',
        header: 'Vendor',
        cell: (props: CellContext<FraudQueueItem, unknown>) => {
          const row = props.row;
          return (
            <div>
              <p className="font-medium text-gray-900">{row.original.vendorName}</p>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded text-xs',
                  row.original.vendorVerificationStatus === 'verified'
                    ? 'bg-green-100 text-green-800'
                    : row.original.vendorVerificationStatus === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                )}
              >
                {row.original.vendorVerificationStatus}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'amountCents',
        header: 'Amount',
        cell: (props: CellContext<FraudQueueItem, unknown>) => (
          <span className="font-medium">{formatCurrency(props.row.original.amountCents)}</span>
        ),
      },
      {
        accessorKey: 'combinedRiskScore',
        header: 'Risk Score',
        cell: (props: CellContext<FraudQueueItem, unknown>) => (
          <RiskScoreBar score={props.row.original.combinedRiskScore} />
        ),
      },
      {
        accessorKey: 'timeInQueueMinutes',
        header: 'Time in Queue',
        cell: (props: CellContext<FraudQueueItem, unknown>) => {
          const minutes = props.row.original.timeInQueueMinutes;
          return (
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1.5 text-gray-400" />
              <span
                className={cn(
                  minutes > 240 && 'text-red-600 font-medium',
                  minutes > 60 && minutes <= 240 && 'text-orange-600',
                  'text-gray-600'
                )}
              >
                {minutes < 60
                  ? `${minutes}m`
                  : minutes < 1440
                  ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
                  : `${Math.floor(minutes / 1440)}d`}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (props: CellContext<FraudQueueItem, unknown>) => {
          const row = props.row;
          return (
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  releasePayment(row.original.paymentId, 'Single release from queue');
                }}
                disabled={isProcessing}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Release"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  denyPayment(row.original.paymentId, 'Single deny from queue');
                }}
                disabled={isProcessing}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Deny"
              >
                <XCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => setSelectedPaymentId(row.original.paymentId)}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                title="View Details"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ],
    [isSelected, isProcessing, toggleSelection, selectAll, deselectAll, items, releasePayment, denyPayment]
  );

  const table = useReactTable({
    data: filteredItems,
    columns,
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
  });

  const handleBulkRelease = async () => {
    await bulkRelease('Bulk release from queue');
    setShowBulkActionConfirm(null);
    deselectAll();
  };

  const handleBulkDeny = async () => {
    await bulkDeny('Bulk deny from queue');
    setShowBulkActionConfirm(null);
    deselectAll();
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Total Held</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalHeld}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">High Risk</p>
            <p className="text-2xl font-bold text-red-600">{stats.highRisk}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Medium Risk</p>
            <p className="text-2xl font-bold text-orange-600">{stats.mediumRisk}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Low Risk</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.lowRisk}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search payments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-colors',
              showFilters
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            )}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {Object.keys(filters).length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {Object.keys(filters).length}
              </span>
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
          </button>
        </div>

        {/* Bulk Actions */}
        {selectedCount > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">{selectedCount} selected</span>
            <button
              onClick={() => setShowBulkActionConfirm('release')}
              className="px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
            >
              Release All
            </button>
            <button
              onClick={() => setShowBulkActionConfirm('deny')}
              className="px-3 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
            >
              Deny All
            </button>
            <button
              onClick={deselectAll}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && <FiltersPanel filters={filters} onFiltersChange={setFilters} stats={stats} />}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              {table.getHeaderGroups().map((headerGroup: HeaderGroup<FraudQueueItem>) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header: Header<FraudQueueItem, unknown>) => (
                    <th
                      key={header.id}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header.isPlaceholder ? null : (
                        <button
                          className={cn(
                            'flex items-center space-x-1 hover:text-gray-700',
                            header.column.getCanSort() && 'cursor-pointer'
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {header.column.getCanSort() && (
                            <span>
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : null}
                            </span>
                          )}
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-sm text-gray-500 mt-2">Loading queue...</p>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center">
                    <Shield className="w-12 h-12 mx-auto text-gray-300" />
                    <p className="text-sm text-gray-500 mt-2">No payments in fraud review queue</p>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row: Row<FraudQueueItem>) => (
                  <tr
                    key={row.id}
                    className={cn(
                      'hover:bg-gray-50 transition-colors',
                      isSelected(row.original.paymentId) && 'bg-blue-50'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {table.getRowModel().rows.length} of {total} payments
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      <FraudDetailPanel
        payment={selectedPayment || null}
        isOpen={!!selectedPaymentId}
        onClose={() => setSelectedPaymentId(null)}
        onRelease={(id) => {
          releasePayment(id, 'Released from detail panel');
          setSelectedPaymentId(null);
        }}
        onDeny={(id) => {
          denyPayment(id, 'Denied from detail panel');
          setSelectedPaymentId(null);
        }}
      />

      {/* Bulk Action Confirmation */}
      {showBulkActionConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {showBulkActionConfirm === 'release' ? 'Release' : 'Deny'} {selectedCount} Payments?
            </h3>
            <p className="text-gray-600 mb-6">
              This action will {showBulkActionConfirm} all selected payments. This cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkActionConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={showBulkActionConfirm === 'release' ? handleBulkRelease : handleBulkDeny}
                disabled={isProcessing}
                className={cn(
                  'px-4 py-2 text-white rounded-lg',
                  showBulkActionConfirm === 'release'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                )}
              >
                {isProcessing ? 'Processing...' : `Confirm ${showBulkActionConfirm === 'release' ? 'Release' : 'Deny'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FraudQueue;
